<template>
	<div class="staticContentViewer">
		<vue-headful
			v-if="ShowHeader"
			:title="StaticContentItem.Title"
			:description="StaticContentItem.MetaDescription"
		/>

		<v-card tile class="elevation-2">
			<v-card flat v-if="ShowCoverimage && !StaticContentLoaded" key="shine">
				<v-responsive aspect-ratio="2.75" class="shine"></v-responsive>
			</v-card>
			<div v-else>
				<v-img
          max-height="400" contain
					v-if="StaticContentItem.ImageFilePath"
					:src="StaticContentItem.ImageFilePath[imageVersion]"
				></v-img>
				<div
					class="articleCoverCaption"
					v-html="StaticContentItem.ImageCaption"
					v-if="StaticContentItem.ImageFilePath && StaticContentItem.ImageCaption"
				></div>
			</div>
			<v-container>
				<v-row>
					<v-col cols="12" class="pa-5">
						<div v-if="ShowBreadcrumbs">
							<v-breadcrumbs
								:items="BreadcrumbsStart"
								class="pl-0"
								v-if="StaticContentLoaded"
							>
                <template v-slot:divider>
                  <v-icon class="grey--text" x-small>fas fa-chevron-right</v-icon>
                </template>
							</v-breadcrumbs>
							<div class="skeleton-text" v-else></div>
						</div>
						<h1 class="nutriyou-h2 primary--text limitWidth1000" >
							<span v-text="StaticContentItem.Title" v-if="StaticContentLoaded"></span>
							<span class="skeleton-text header" v-else></span>
						</h1>

						<div
							class="ArticleBody text-justify"
							v-html="StaticContentItem.Text"
							v-if="StaticContentLoaded"
						></div>
						<skeleton-text v-else :lines="6" alignment="left"></skeleton-text>

						<div class="mt-4" v-if="ShowTags && StaticContentLoaded">
							<!--TODO: Navigate to tags Page-->
							<v-chip
								color="grey darken-3"
								text-color="white"
								label
								v-for="tag in Tags"
								:key="tag.TagId"
								class="mr-2 mt-2"
							>
								{{ tag.TagName }}
								<v-icon right>fas fa-tag</v-icon>
							</v-chip>
						</div>
						<div v-if="ShowTags && !StaticContentLoaded">
							<span
								v-for="n in 4"
								:key="n"
								class="skeleton-text chip short my-2 mr-2"
							></span>
						</div>

						<slot name="afterContent"></slot>
					</v-col>
				</v-row>
			</v-container>
		</v-card>
	</div>
</template>
<script>
import CrudClient from "@/services/CrudClient/";
import { mapActions } from "vuex";

export default {
	metaInfo() {
		return {
			title: this.StaticContentItem.Title,
			meta: [{ name: "description", content: this.StaticContentItem.MetaDescription }],
		};
	},
	name: "StaticContentViewer",
	props: {
		ContentId: Number,

		ShowBreadcrumbs: {
			type: Boolean,
			default: false,
		},

		ShowCoverimage: {
			type: Boolean,
			default: false,
		},

		ShowTags: {
			type: Boolean,
			default: false,
		},

		ShowHeader: {
			type: Boolean,
			default: false,
		},

		BreadcrumbsStart: {
			type: Array,
			default: () => [
				{
					text: "Home",
					disabled: false,
					exact: true,
					to: "/",
				},
				{
					text: "Blog",
					disabled: false,
					exact: true,
					to: "/Blog",
				}
			],
		},
	},
	data: () => ({
		StaticContentLoaded: false,
		StaticContentItem: {},
		StaticContentService: new CrudClient("StaticContent"),
		Tags: [],
	}),
	computed: {
		imageVersion: function () {
			switch (this.$vuetify.breakpoint.name) {
				case "xs":
					return 400;
				case "sm":
					return 800;
				case "md":
				case "lg":
				case "xl":
				default:
					return 1200;
			}
		},
	},
	methods: {
		...mapActions(["snackError"]),

		async LoadStaticContent(ContentId) {
			try {
				this.StaticContentLoaded = false;

				this.StaticContentItem = await this.StaticContentService.GetSelectedFields(
					ContentId,
					"Title, Text, ImageFilePath, FriendlyUrl, MetaDescription, ImageCaption"
				);

				if (this.ShowTags) {
					this.Tags = await this.StaticContentService.GetTags(ContentId);
				}
				if (this.ShowBreadcrumbs) {
					this.BreadcrumbsStart.push({
						text: this.StaticContentItem.Title,
						disabled: true,
						exact: true,
						to: this.StaticContentItem.FriendlyUrl,
					});
				}

				this.StaticContentLoaded = true;
			} catch (error) {
				this.snackError({ Text: this.$t("snackError.LoadStaticContent") });
				this.$captureError(error);
			}
		},
	},
	async mounted() {
		this.$log.info("StaticContentViewer Mounted");
		await this.LoadStaticContent(this.ContentId);
	},
};
</script>
<style lang="scss">
@import "@/assets/css/variables.scss";
.ArticleBody {
	img {
		display: block;
		max-width: 100%;
	}

	p{
		margin-bottom: 30px;
	}
}

.ArticleBody {
	// Base Styles
	font-family: $font_Text, serif; // Choose a serif font for readability
	line-height: 1.6;
	font-size: 1rem;
	color: #424242; // Dark gray color for base text
	max-width: 1000px; // Optional: Set a max width for the article
	margin: 0 auto; // Center the article within the container

	// Headings
	h1, h2 {
		font-family: $font_Title; // A different font for headings
		margin-top: 2rem;
		margin-bottom: 1rem;
		line-height: 1.4;
		color: #222; // Slightly darker color for headings
	}

	h3, h4, h5, h6 {
		font-family: $font_Text; // A different font for headings
		margin-top: 2rem;
		margin-bottom: 1rem;
		line-height: 1.4;
		font-style: normal;
		color: #222; // Slightly darker color for headings
	}

	h1 {
		font-size: 2.5rem;
	}

	h2 {
		font-size: 2rem;
	}

	h3 {
		font-size: 1.75rem;
		font-weight: 700; // bold
		color: #424242
	}

	h4 {
		font-size: 1.3rem;
		margin-bottom: 0.8rem;

		font-weight: 600; // semi bold
		font-style: normal;
	}

	h5 {
		font-size: 1.1rem;
		margin-bottom: 0.8rem;
		font-weight: 500; // semi bold
	}

	h6 {
		font-size: 0.9rem;
		margin-bottom: 0.8rem;
	}

	// Paragraphs
	p {
		margin-bottom: 1.5rem;
		line-height: 1.8; // Increase line height for better readability
	}

	// Links
	a {
		color: #0077cc; // Accentuate links with a bright color
		text-decoration: none;
		font-weight: bold;

		&:hover {
			text-decoration: underline;
			color: #005fa3; // Darker shade for hover effect
		}
	}

	// Lists
	ul, ol {
		margin: 1.5rem 0 1.5rem 2rem; // Add space around lists
		padding: 0;

		li {
			margin-bottom: 0.75rem; // Slight gap between list items

			&:last-child {
				margin-bottom: 0; // Remove gap for the last item
			}
		}

		ol {
			list-style-type: decimal; // Numbered list for ordered lists
		}

		ul {
			list-style-type: disc; // Bulleted list for unordered lists
		}
	}

	// Blockquotes
	blockquote {
		margin: 2rem 0;
		padding: 1rem 1.5rem;
		font-style: italic;
		background-color: #f9f9f9;
		border-left: 4px solid #0077cc;

		p {
			margin: 0; // No outer spacing for blockquote paragraphs
		}
	}

	// Code Blocks
	pre {
		margin: 1.5rem 0;
		padding: 1rem;
		font-family: 'Courier New', monospace; // Monospace font for code
		font-size: 0.95rem;
		line-height: 1.5;
		background-color: #f7f7f7;
		border: 1px solid #e1e1e1;
		border-radius: 4px;
		overflow-x: auto;
	}

	code {
		font-family: 'Courier New', monospace;
		font-size: 0.95rem;
		background-color: #f2f2f2;
		padding: 0.2rem 0.4rem;
		border-radius: 4px;
	}

	// Images
	img {
		max-width: 100%; // Make images responsive
		height: auto;
		margin: 1.5rem 0;
		border-radius: 4px; // Slight rounded corners
		box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1); // Optional shadow for styling
	}

	// Tables
	table {
		width: 100%;
		border-collapse: collapse;
		margin: 1.5rem 0;

		th, td {
			padding: 0.75rem;
			text-align: left;
			border: 1px solid #e1e1e1; // Add borders for table cells
		}

		th {
			background-color: #f9f9f9; // Slightly darker background for table headers
			font-weight: bold;
		}

		td {
			background-color: #fff;
		}
	}
}
</style>
